import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { CheckoutFailReason, ContactType } from "ipctg-inflow-types/InFlowEnums"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import StripeElementsWrapper from "@/globalComponents/molecules/StripeElementsWrapper"
import {
  ExpectedError,
  ParkPaymentIntentElementsError,
} from "@/utilities/expectedErrors"
import { parkPaymentIntentElementsQueryOptions } from "@/utilities/hooks/queries/park"
import { useStripePaymentElement } from "@/utilities/hooks/useStripePaymentElement"
import { log } from "@/utilities/logging"
import { prettyCurrencyNumber } from "@/utilities/normalizers"

import { validateContactSearchParams } from "./route"

export const Route = createFileRoute("/qr/$qrLookupID/park/$epoch/checkout")({
  staticData: {
    pageName: "Checkout",
  },
  validateSearch: validateContactSearchParams,
  beforeLoad: async ({ params, context, search }) => {
    const { rate } = context.sessionState
    const { contactType, email, phone } = search

    if (!rate || !rate.rateProductID) {
      log({
        level: "warn",
        flow: "park",
        scope: "guard.rate",
        message:
          "No rate information found on /checkout, redirecting to /welcome",
        meta: { sessionState: context.sessionState },
      })
      throw redirect({ to: "/qr/$qrLookupID/welcome", params })
    }

    if (
      (contactType === ContactType.Email && !email) ||
      (contactType === ContactType.Phone && !phone)
    ) {
      log({
        level: "error",
        flow: "park",
        scope: "guard.contact",
        message: `No contact information found on /cart for ${contactType}`,
        meta: { search },
      })
      throw new ParkPaymentIntentElementsError(
        CheckoutFailReason.InvalidRequestFormat
      )
    }

    let paymentIntent
    try {
      paymentIntent = await context.queryClient.fetchQuery(
        parkPaymentIntentElementsQueryOptions({
          email:
            contactType === ContactType.Email
              ? email || context.sessionState.email || null
              : null,
          phone:
            contactType === ContactType.Phone
              ? phone || context.sessionState.phoneNumber || null
              : null,
          contactType,
          epoch: params.epoch,
          rateID: rate.rateProductID || "",
        })
      )
    } catch (e) {
      if (e instanceof ParkPaymentIntentElementsError) {
        if (e.failReason === CheckoutFailReason.PaymentAlreadyCompleted) {
          log({
            level: "warn",
            flow: "park",
            scope: "guard.paymentIntent",
            message:
              "Payment already completed, redirecting to /payment-complete",
          })
          throw redirect({
            to: "/qr/$qrLookupID/park/$epoch/payment-complete",
            params,
          })
        }
      }
      throw e
    }

    // If no payment required, immediately redirect to confirmation
    if (!paymentIntent.requiresPayment) {
      throw redirect({
        to: "/qr/$qrLookupID/park/$epoch/confirmation",
        params: {
          qrLookupID: params.qrLookupID,
          epoch: params.epoch,
        },
      })
    }

    return { paymentIntent }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID, epoch } = Route.useParams()
  const { paymentIntent } = Route.useRouteContext()
  const { t } = useTranslation(["park", "common", "errors"])

  // At this point, we know payment is required (free parking redirects in beforeLoad)
  // and payment is not yet complete (session status check in beforeLoad)
  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("cart.checkout", { ns: "park" })}
      </Heading>

      <StripeElementsWrapper
        clientSecret={paymentIntent.clientSecret!}
        customerSessionClientSecret={
          paymentIntent.customerSessionClientSecret || undefined
        }
        flow="park"
      >
        <Card>
          <Heading className="center" level={1} as={2}>
            {t("cart.paymentDetails", {
              ns: "park",
            })}
          </Heading>
          <CheckoutForm
            amountDue={paymentIntent.amountDue || 0}
            goNext={() =>
              navigate({
                to: "/qr/$qrLookupID/park/$epoch/confirmation",
                params: { qrLookupID, epoch: epoch },
              })
            }
          />
        </Card>
      </StripeElementsWrapper>
    </>
  )
}

interface CheckoutFormProps {
  amountDue: number
  goNext: () => void
}

function CheckoutForm(props: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation(["park", "common", "errors"])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()

  const { paymentElementProps, stripeError } = useStripePaymentElement({
    flow: "park",
  })

  if (stripeError) throw stripeError

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <PaymentElement {...paymentElementProps} />
        <Separator />
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
          }}
        >
          <Heading level={4} as={3}>
            {t("common:cart.dueToday")}
          </Heading>
          <Text level={2} style={{ marginLeft: "var(--space-2)" }}>
            {prettyCurrencyNumber(Math.max(0, props.amountDue))}
          </Text>
        </div>
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey, { ns: "errors" })
              : error.message}
          </FieldError>
        )}

        <Button type="submit" buttonType="primary" isPending={loading}>
          {t("common.submit", { ns: "common" })}
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)

    if (!stripe || !elements) {
      setLoading(false)
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error(t("common.paymentDetailsNotFound", { ns: "errors" })))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      })

      if (result.error) {
        log({
          level: "error",
          flow: "park",
          scope: "payment.stripeConfirmPayment",
          message: "Stripe confirmPayment returned an error",
          meta: {
            code: result.error.code,
            type: result.error.type,
            declineCode: result.error.decline_code,
          },
          sentry: "exception",
          exception: result.error,
        })
        setError(new Error(result.error.message))
        return
      }

      // Wait 1 second to give Stripe time to process
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Navigate to confirmation page which will call /pay
      props.goNext()
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "park",
        scope: "payment.confirmPayment",
        message: "Park payment confirmation failed",
        meta: { error: e },
        sentry: e instanceof ExpectedError ? "breadcrumb" : "exception",
        exception: e instanceof Error ? e : new Error(String(e)),
      })
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      }
    } finally {
      setLoading(false)
    }
  }
}
